import z from "zod";

const dataSyncErrorTypeValues = [
  "PRODUCTION_BRANCH_ANONYMIZATION_ATTEMPT",
  "NON_BUILD_BRANCH_MUTATION_ATTEMPT",
  "BACKUP_NOT_FOUND",
  "PREVIEW_BRANCH_NOT_FOUND",
  "PREVIEW_STALE",
  "PORTAL_LINK_RESET_IN_PRODUCTION_ATTEMPT",
  "CLERK_ID_OVERWRITE_IN_PRODUCTION_ATTEMPT",
  "PROXY_PHONE_RESET_IN_PRODUCTION_ATTEMPT",
  "SCRUB_PLAN_INVALID",
  "SCRUB_FAILED",
  "SCRUB_VERIFICATION_FAILED",
  "SCHEMA_SYNC_DIFF_FAILED",
  "SCHEMA_SYNC_DRIFT_TOO_LARGE",
  "SCHEMA_SYNC_MIGRATION_FAILED",
  "SCHEMA_SYNC_SOURCE_NOT_CONFIGURED",
  "NON_ESSENTIAL_STEP_FAILED",
  "PREVIEW_BRANCH_NAME_READ_FAILED",
  "PREVIEW_DOPPLER_WRITE_FAILED",
  "PARENT_BRANCH_READY_TIMEOUT",
] as const;

const dataSyncErrorTypeSchema = z.enum(dataSyncErrorTypeValues);
const DataSyncErrorType = dataSyncErrorTypeSchema.enum;
type DataSyncErrorType = z.infer<typeof dataSyncErrorTypeSchema>;

type ErrorDetails = {
  sentryDescription: string;
};

export type DataSyncErrors =
  | {
      errorType: typeof DataSyncErrorType.PRODUCTION_BRANCH_ANONYMIZATION_ATTEMPT;
      data: {
        branch: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.NON_BUILD_BRANCH_MUTATION_ATTEMPT;
      data: {
        branchName: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.BACKUP_NOT_FOUND;
      data: {
        branchName: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.PREVIEW_BRANCH_NOT_FOUND;
      data: {
        secret: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.PREVIEW_STALE;
      data: {
        branch: string;
        ageHours: number;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.PORTAL_LINK_RESET_IN_PRODUCTION_ATTEMPT;
      data: {
        branch: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.CLERK_ID_OVERWRITE_IN_PRODUCTION_ATTEMPT;
      data: {
        branch: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.PROXY_PHONE_RESET_IN_PRODUCTION_ATTEMPT;
      data: {
        branch: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.SCRUB_PLAN_INVALID;
      data: {
        problem: string;
        [detail: string]: string | number;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.SCRUB_FAILED;
      data: {
        failedTasks: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.SCRUB_VERIFICATION_FAILED;
      data: {
        violations: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.SCHEMA_SYNC_DIFF_FAILED;
      data: {
        detail: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.SCHEMA_SYNC_DRIFT_TOO_LARGE;
      data: {
        statementCount: number;
        max: number;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.SCHEMA_SYNC_SOURCE_NOT_CONFIGURED;
      data: {
        variable: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.SCHEMA_SYNC_MIGRATION_FAILED;
      data: {
        migrationUuid: string;
        status: string;
        message: string;
        statement: string;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.NON_ESSENTIAL_STEP_FAILED;
      data: {
        step: string;
        error: string;
        statusCode?: number;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.PREVIEW_BRANCH_NAME_READ_FAILED;
      data: {
        error: string;
        statusCode?: number;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.PREVIEW_DOPPLER_WRITE_FAILED;
      data: {
        branch: string;
        error: string;
        statusCode?: number;
      };
    }
  | {
      errorType: typeof DataSyncErrorType.PARENT_BRANCH_READY_TIMEOUT;
      data: {
        branchName: string;
        elapsedMs: number;
      };
    };

export const dataSyncErrorDetails: Record<DataSyncErrorType, ErrorDetails> = {
  [DataSyncErrorType.PRODUCTION_BRANCH_ANONYMIZATION_ATTEMPT]: {
    sentryDescription: "Attempted to anonymize data on a production branch",
  },
  [DataSyncErrorType.NON_BUILD_BRANCH_MUTATION_ATTEMPT]: {
    sentryDescription: "Preview build tried to rename or delete a branch it does not own",
  },
  [DataSyncErrorType.BACKUP_NOT_FOUND]: {
    sentryDescription: "No successful PlanetScale backup found",
  },
  [DataSyncErrorType.PREVIEW_BRANCH_NOT_FOUND]: {
    sentryDescription: "No live preview branch is recorded in Doppler",
  },
  [DataSyncErrorType.PREVIEW_STALE]: {
    sentryDescription: "The live preview branch is older than a weekly rotation should allow",
  },
  [DataSyncErrorType.PORTAL_LINK_RESET_IN_PRODUCTION_ATTEMPT]: {
    sentryDescription: "Attempted to reset portal links in production",
  },
  [DataSyncErrorType.CLERK_ID_OVERWRITE_IN_PRODUCTION_ATTEMPT]: {
    sentryDescription: "Attempted to overwrite Clerk IDs in production",
  },
  [DataSyncErrorType.PROXY_PHONE_RESET_IN_PRODUCTION_ATTEMPT]: {
    sentryDescription: "Attempted to reset proxy phone data in production",
  },
  [DataSyncErrorType.SCRUB_PLAN_INVALID]: {
    sentryDescription: "The PII registry does not cover the schema the preview build is scrubbing",
  },
  [DataSyncErrorType.SCRUB_FAILED]: {
    sentryDescription: "One or more scrub tasks failed on the preview build branch",
  },
  [DataSyncErrorType.SCRUB_VERIFICATION_FAILED]: {
    sentryDescription: "Rows that should have been scrubbed remain before the preview branch was published",
  },
  [DataSyncErrorType.SCHEMA_SYNC_DIFF_FAILED]: {
    sentryDescription: "Could not diff the preview branch schema against PlanetScale main",
  },
  [DataSyncErrorType.SCHEMA_SYNC_DRIFT_TOO_LARGE]: {
    sentryDescription: "Preview schema drift exceeds what the automatic sync will apply",
  },
  [DataSyncErrorType.SCHEMA_SYNC_SOURCE_NOT_CONFIGURED]: {
    sentryDescription: "The read-only URL for PlanetScale main is not configured for the preview schema sync",
  },
  [DataSyncErrorType.SCHEMA_SYNC_MIGRATION_FAILED]: {
    sentryDescription: "A Vitess online schema migration on the preview branch failed",
  },
  [DataSyncErrorType.NON_ESSENTIAL_STEP_FAILED]: {
    sentryDescription: "A non-essential step in the data sync pipeline failed",
  },
  [DataSyncErrorType.PREVIEW_BRANCH_NAME_READ_FAILED]: {
    sentryDescription: "Failed to read the live preview branch name from Doppler",
  },
  [DataSyncErrorType.PREVIEW_DOPPLER_WRITE_FAILED]: {
    sentryDescription: "Failed to write the verified preview branch credentials to Doppler",
  },
  [DataSyncErrorType.PARENT_BRANCH_READY_TIMEOUT]: {
    sentryDescription: "Preview build branch never became ready before the wait ceiling",
  },
};
